import type { LangCode } from '@/lib/lang';
import type { ProviderId, ProviderManifest, SpeechKind } from './types';
import { cambaiManifest } from './providers/cambai/manifest';
import { iflytekManifest } from './providers/iflytek/manifest';
import { qwenManifest } from './providers/qwen/manifest';
import { platformaiManifest } from './providers/platformai/manifest';

/**
 * The single list every part of the app reads from. Adding a provider means
 * adding its manifest here plus one line in each registry — nothing else.
 */
export const speechManifests: ProviderManifest[] = [
  cambaiManifest,
  iflytekManifest,
  qwenManifest,
  platformaiManifest,
];

const byId = new Map<ProviderId, ProviderManifest>(speechManifests.map((m) => [m.id, m]));

export function getManifest(id: ProviderId | undefined | null): ProviderManifest | undefined {
  return id ? byId.get(id) : undefined;
}

export function listManifests(kind: SpeechKind): ProviderManifest[] {
  return speechManifests.filter((m) => m.capabilities[kind]);
}

export function supportsCapability(id: ProviderId, kind: SpeechKind): boolean {
  return Boolean(getManifest(id)?.capabilities[kind]);
}

export function supportsLanguage(id: ProviderId, kind: SpeechKind, language: LangCode): boolean {
  const manifest = getManifest(id);
  if (!manifest || !manifest.capabilities[kind]) return false;
  const allowed = manifest.languages[kind];
  return allowed === null || allowed.includes(language);
}

/** Label for the header badge; falls back to the raw id so an unknown provider is still visible. */
export function shortLabelFor(id: ProviderId): string {
  return getManifest(id)?.shortLabel ?? id;
}
